import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faStarHalfAlt } from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../../context/userContext";

const RatingModal = ({
  isOpen,
  onClose,
  userRating,
  setUserRating,
  handleRatingSubmit,
  errorMessage,
}) => {
  if (!isOpen) return null;

  const handleSubmit = () => {
    handleRatingSubmit(userRating);
  };

  const handleStarClick = (rating) => {
    setUserRating(rating);
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-md shadow-lg w-80">
        <h3 className="text-xl font-semibold mb-4">Rate this Product</h3>
        <div className="flex justify-center mb-4">
          {[...Array(5)].map((_, index) => {
            const starValue = index + 1;

            return (
              <FontAwesomeIcon
                key={index}
                icon={starValue <= userRating ? faStar : faStar}
                className={`text-2xl cursor-pointer mx-1 ${
                  starValue <= userRating ? "text-yellow-500" : "text-gray-300"
                }`}
                onClick={() => handleStarClick(starValue)}
              />
            );
          })}
        </div>
        <p className="text-center mb-4">{userRating} / 5</p>
        {errorMessage && (
          <p className="text-center text-red-500 mb-4">{errorMessage}</p>
        )}
        <div className="flex justify-between">
          <button
            className="bg-gray-300 px-4 py-2 rounded-md"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded-md"
            onClick={handleSubmit}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductReviews = ({ productId }) => {
  const { user } = useUser();

  const [ratings, setRatings] = useState({
    averageRating: 0,
    totalRatings: 0,
    ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  });
  const [userRating, setUserRating] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchProductRating(productId);
  }, [productId]);

  const fetchProductRating = async (productId) => {
    try {
      const response = await axios.get(
        `https://api.theonlysale.com/api/product-ratings/getByProduct/${productId}`
      );
      setRatings(response.data);
    } catch (error) {
      console.error("Error fetching product ratings:", error);
    }
  };

  const handleRatingSubmit = async (ratingData) => {
    if (!user || !user.id || !productId) {
      setErrorMessage("You need to be logged in to rate a product."); 
      return;
    }

    try {
      console.log("Submitting rating with:", {
        ratingData,
        productId,
        userId: user.id,
      });
      const response = await axios.post(
        `https://api.theonlysale.com/api/product-ratings/add`,
        { rating: ratingData, productId, userId: user.id }
      );
      console.log("Rating submitted successfully:", response.data);
      fetchProductRating(productId);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error submitting rating:", error);
    }
  };

  const handleSubmit = () => {
    if (user && user.id) {
      handleRatingSubmit(userRating);
      setIsModalOpen(false);
    } else {
      setErrorMessage("You need to be logged in to rate a product."); 
    }
  };

  const averageRating =
    typeof ratings.averageRating === "number" ? ratings.averageRating : 0;

  return (
    <div className="p-6 bg-white dark:bg-gray-900 shadow-md">
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <h2 className="text-2xl font-bold dark:text-white">Product Rating</h2>
          <div className="ml-4 flex items-center text-yellow-500">
            {[...Array(5)].map((_, index) => {
              const ratingValue = index + 1;
              // Full stars
              if (ratingValue <= Math.floor(ratings.averageRating)) {
                return (
                  <FontAwesomeIcon key={index} icon={faStar} className="mr-1" />
                );
              }
              else if (
                ratingValue === Math.ceil(ratings.averageRating) &&
                ratings.averageRating % 1 !== 0
              ) {
                return (
                  <FontAwesomeIcon
                    key={index}
                    icon={faStarHalfAlt}
                    className="mr-1"
                  />
                );
              }
              return (
                <FontAwesomeIcon
                  key={index}
                  icon={faStar}
                  className="mr-1 text-gray-300"
                />
              );
            })}
            ({ratings.averageRating || "0.00"})
          </div>
        </div>

        <button
          className="bg-blue-500 text-white px-4 py-2 rounded-md"
          onClick={() => {
            setErrorMessage("");
            setIsModalOpen(true);
          }}
        >
          Rate Product
        </button>
      </div>

      <div className="mt-6 max-w-md">
        <h3 className="text-lg font-semibold dark:text-white mb-4">
          Rating Breakdown
        </h3>
        {[5, 4, 3, 2, 1].map((stars) => {
          const ratingCount = ratings.ratingDistribution[stars] || 0;

          return (
            <div key={stars} className="flex items-center mb-2">
              <div className="flex items-center w-44">
                {[...Array(stars)].map((_, index) => (
                  <FontAwesomeIcon
                    key={index}
                    icon={faStar}
                    className="text-yellow-500"
                  />
                ))}
              </div>
              <div className="w-full h-4 bg-gray-200 rounded-lg ml-2 mr-2">
                <div
                  className="h-4 bg-blue-600 rounded-lg"
                  style={{
                    width: `${
                      ratings.totalRatings
                        ? (ratingCount / ratings.totalRatings) * 100
                        : 0
                    }%`,
                  }}
                ></div>
              </div>
              <span className="dark:text-white text-sm pl-2">
                {ratingCount}
              </span>
            </div>
          );
        })}
      </div>

      <RatingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userRating={userRating}
        setUserRating={setUserRating}
        handleRatingSubmit={handleRatingSubmit}
        errorMessage={errorMessage}
      />
    </div>
  );
};

export default ProductReviews;
